import React, { useState, useEffect, useRef, useCallback } from 'react';
import PdfGen from './PdfGen';
import TableFormat from './TableFormat';
import PhotoUpload from './PhotoUpload';
import Modal from 'react-modal';
import api from "./api";
import './App.css';
import CameraScan from './CameraScan';
import SettingWheel from './SettingWheel';
import AdminPanel from './AdminPanel';
import { useMsal } from "@azure/msal-react";
import CompletedOrderTable from "./CompletedOrderTable";
import errorReason from './errorReason';
import { shrinkItemPhotos, shrinkPhotoList } from './imageCompress';

// The backend (Kestrel) rejects request bodies over 30,000,000 bytes by
// cutting the connection, which the browser reports as a bare "Network Error".
// Stay under it with a margin and say why, instead of letting that happen.
const MAX_SAVE_MB = 28;

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────

const LoadingOverlay = ({ isLoading, message }) => {
  if (!isLoading) return null;
  return (
    <div className="loading-overlay">
      <div className="spinner" />
      <p>{message}</p>
    </div>
  );
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const formatItemFromApi = ({ barcode, barcodeValue, quantity, setQuantity, subSetQuantity, location, hasItemGroup }) => ({
  barcode,
  barcodeValue,
  quantity,
  setQuantity: setQuantity || 1,
  subSetQuantity: subSetQuantity || 1,
  location: location || null,
  scannedCount: 0,
  individualScanned: 0,
  subSetScanned: 0,
  setScanned: 0,
  status: 'not-scanned',
  isManual: hasItemGroup || false,
});

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

function FormFunction({ user, onLogout, showSettingsModal, setShowSettingsModal, initialOrderID, onInitialOrderIDHandled }) {
  // ── State ──────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
const [pdfResolution, setPdfResolution] = useState('medium');
  const [orderID, setOrderID] = useState('');
  const [orderStatus, setOrderStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [completedItems, setCompletedItems] = useState([]);
  const [toggleModes, setToggleModes] = useState({});
  const [itemPhotos, setItemPhotos] = useState({});
  const [orderPhotos, setOrderPhotos] = useState([]);
  const [pdfEmail, setPdfEmail] = useState('');
const [pdfDestinations, setPdfDestinations] = useState(['email', 'sharepoint', 'striven', 'local']);

  const [submitted, setSubmitted] = useState(false);
  const [resumedDraft, setResumedDraft] = useState(null); // { savedAt } when the current session was auto-resumed from a saved draft
  const [showAdminLevel2, setShowAdminLevel2] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [confirmedCompletion, setConfirmedCompletion] = useState(false);
  const [awaitingFinalConfirmation, setAwaitingFinalConfirmation] = useState(false);
  const [showWarehouseLocation, setShowWarehouseLocation] = useState(true);

  const [useCamera, setUseCamera] = useState(false);
  const [scanMode] = useState('single'); // 'single' | 'mass'  (setter unused but kept for future)
  const [lastScanned, setLastScanned] = useState({ code: '', timestamp: 0 });
  const [pendingBarcode, setPendingBarcode] = useState('');
const [alertModal, setAlertModal] = useState(null);

  const [quantityInputModal, setQuantityInputModal] = useState(false);
const [maxPhotosPerItem, setMaxPhotosPerItem] = useState(4);
  const [orderPhotoBusy, setOrderPhotoBusy] = useState(false); // photos still being processed on the submit screen
  const [cartonPrompt, setCartonPrompt] = useState(null); // { rowKey, barcode, currentValue }
  // ── Refs ───────────────────────────────────
  const cameraRef = useRef(null);
  const cartonInputRef = useRef(null);
  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const scanHandlerRef = useRef(null);
const [requireItemPhotos, setRequireItemPhotos] = useState(true);
const [requireOrderPhoto, setRequireOrderPhoto] = useState(true);
const [startedAt, setStartedAt] = useState(null);
  // ── MSAL ───────────────────────────────────
  Modal.setAppElement('#root');
  const { instance, accounts } = useMsal();
  const account = accounts[0];
const startedAtRef = useRef(null);
  // ── Effects ────────────────────────────────

  useEffect(() => {
    scanHandlerRef.current = handleScan;
  });

  useEffect(() => {
    const SCAN_RESET_TIME = 300;
const handleKeyDown = (e) => {
  // ← Add this: ignore scanner input when user is typing in a field
  const tag = document.activeElement?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

  const now = Date.now();

  if (now - lastKeyTimeRef.current > SCAN_RESET_TIME) {
    bufferRef.current = '';
  }
  lastKeyTimeRef.current = now;

  if (e.key === 'Enter' || e.key === 'Tab' || e.code === 'NumpadEnter') {
    if (bufferRef.current.length > 0) {
      const code = bufferRef.current;
      requestAnimationFrame(() => scanHandlerRef.current(code));
      bufferRef.current = '';
    }
    return;
  }

  if (e.ctrlKey || e.altKey || e.metaKey) return;
  if (e.key.length === 1) bufferRef.current += e.key;
};

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
const [setLabel, setSetLabel] = useState('Carton');

useEffect(() => {
  const loadSettings = async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await api.get('/api/config/credentials', { headers });
      setRequireItemPhotos(res.data.requireItemPhotos ?? true);
setRequireOrderPhoto(res.data.requireOrderPhoto ?? true);
      setSetLabel(res.data.setLabel ?? 'Carton');  // 👈 add this line
      setMaxPhotosPerItem(res.data.maxPhotosPerItem ?? 4);
      setPdfResolution(res.data.pdfResolution ?? 'medium');
      setPdfDestinations(res.data.pdfDestinations ?? ['email', 'sharepoint', 'striven', 'local']);
      setShowWarehouseLocation(res.data.showWarehouseLocation ?? true);
    } catch {
      // non-critical
    }
  };
  loadSettings();
}, []);
  // ── Auth helpers ───────────────────────────

  const getAuthHeaders = async () => {
    const authProvider = localStorage.getItem('authProvider') || 'microsoft';

    if (authProvider === 'google') {
      const token = localStorage.getItem('authToken');
      return { Authorization: `Bearer ${token}` };
    }

    const configRes = await api.get('/api/config/azure');
    const { clientId } = configRes.data;
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: [`api://${clientId}/access_as_user`],
      account,
    });
    return { Authorization: `Bearer ${tokenResponse.accessToken}` };
  };

  const getGoogleAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // ── State helpers ──────────────────────────

  const resetApp = () => {
    setOrderID('');
    setItems([]);
    setCompletedItems([]);
    setSubmitted(false);
    setShowAdminLevel2(false);
    setVerificationComplete(false);
    setOrderStatus({});
    setToggleModes({});
    setItemPhotos({});
    setOrderPhotos([]);
    setResumedDraft(null);
  };

  const getOrderJson = (photos = itemPhotos, finalPhotos = orderPhotos) => ({
    ItemsJson: JSON.stringify(items),
    ToggleModesJson: JSON.stringify(toggleModes),
    ItemPhotosJson: JSON.stringify(photos),
    OrderPhotosJson: JSON.stringify(finalPhotos),
  });

const showError = (title, message) => {
  setAlertModal({ title, message });
};
  // ── Photo validation ───────────────────────

const getPhotoValidationErrors = () => {
  const errors = [];
  if (requireItemPhotos) {
    const missingItems = items.filter(
      item => !itemPhotos[item.rowKey] || itemPhotos[item.rowKey].length === 0
    );
    if (missingItems.length > 0)
      errors.push({ type: 'items', items: missingItems.map(item => item.barcode) });
  }
  if (requireOrderPhoto && orderPhotos.length === 0)
    errors.push({ type: 'order', message: 'A photo of the full order is required.' });
  return errors;
};
  const canSubmitOrder = () => getPhotoValidationErrors().length === 0;

  // ── Modal handlers ─────────────────────────

const handleCloseModal = () => {
  setAlertModal(null);
  if (useCamera) cameraRef.current?.startScanner();
};
  const handleCloseQuantityModal = () => {
    setQuantityInputModal(false);
    if (useCamera) cameraRef.current?.startScanner();
  };

  // ── Scan logic ─────────────────────────────

  const matchesItem = (item, cleanScan) =>
    item.barcode?.trim().toUpperCase() === cleanScan ||
    item.barcodeValue?.trim().toUpperCase() === cleanScan;

  const computeScanCounts = (item, mode, delta = 1) => {
    const setQuantity = item.setQuantity || 1;
    const subSetQuantity = item.subSetQuantity || 1;

    let individual = item.individualScanned || 0;
    let subSetCount = item.subSetScanned || 0;
    let setCount = item.setScanned || 0;
    let total = item.scannedCount || 0;

    if (mode === 'set') {
      setCount += delta;
      total += delta * setQuantity;
    } else if (mode === 'subset') {
      subSetCount += delta;
      total += delta * subSetQuantity;
    } else {
      individual += delta;
      total += delta;
    }

    return { individual, subSetCount, setCount, total };
  };

  const buildUpdatedItem = (item, { individual, subSetCount, setCount, total }) => {
    const safeTotal = Math.max(0, total);
    const newStatus =
      safeTotal >= item.quantity ? 'scanned' :
      safeTotal > 0 ? 'incomplete' :
      'not-scanned';

    return {
      ...item,
      scannedCount: safeTotal,
      individualScanned: Math.max(0, individual),
      subSetScanned: Math.max(0, subSetCount),
      setScanned: Math.max(0, setCount),
      status: newStatus,
    };
  };

  const handleScan = (scannedBarcode) => {
    const cleanScan = scannedBarcode.trim().toUpperCase();
    const candidates = items.filter(item => matchesItem(item, cleanScan));

    if (candidates.length === 0) {
      showError('Unknown Item', `Item "${scannedBarcode}" not recognized.`);
      return;
    }

    // When the same barcode appears on more than one row (e.g. the same item split
    // across two order groups), a single physical scan must only credit ONE of them —
    // prefer the first row that isn't already fully scanned.
    const targetItem = candidates.find(item => item.scannedCount < item.quantity) || candidates[0];

    const updatedItems = items.map(item => {
      if (item.rowKey !== targetItem.rowKey) return item;

      const mode = toggleModes?.[item.rowKey] || 'single';
      const counts = computeScanCounts(item, mode);

      if (counts.total > item.quantity) {
        showError('Too Many Items', `That would exceed the quantity for "${item.barcode}".`);
        return item;
      }

      return buildUpdatedItem(item, counts);
    });

    setItems(updatedItems);
  };

const handleCamScan = useCallback(async (scannedBarcode) => {
  const now = Date.now();
  if (scannedBarcode === lastScanned.code && now - lastScanned.timestamp < 1000) return;
  setLastScanned({ code: scannedBarcode, timestamp: now });

  const cleanScan = scannedBarcode.trim().toUpperCase();
  const candidates = items.filter(item => matchesItem(item, cleanScan));

  if (candidates.length === 0) {
    await cameraRef.current?.stopScanner();
    showError('Unknown Item', `Item "${scannedBarcode}" not recognized.`);
    return;
  }

  // Same reasoning as handleScan: a duplicate barcode must only credit one row per scan.
  const matchedItem = candidates.find(item => item.scannedCount < item.quantity) || candidates[0];

  if (matchedItem.scannedCount >= matchedItem.quantity) {
    await cameraRef.current?.stopScanner();
    showError('Item Complete', `Item "${matchedItem.barcode}" has already been fully scanned.`);
    return;
  }

  if (scanMode === 'mass') {
    await cameraRef.current?.stopScanner();
    setPendingBarcode(scannedBarcode);
    setQuantityInputModal(true);
    return;
  }

  const mode = toggleModes?.[matchedItem.rowKey] || 'single';
  const incrementBy =
    mode === 'set' ? (matchedItem.setQuantity || 1) :
    mode === 'subset' ? (matchedItem.setQuantity || 1) :
    1;

  const updatedItems = items.map(item => {
    if (item.rowKey !== matchedItem.rowKey) return item;
    const newScannedCount = item.scannedCount + incrementBy;
    const newStatus =
      newScannedCount >= item.quantity ? 'scanned' :
      newScannedCount > 0 ? 'incomplete' : 'not-scanned';
    let individual = item.individualScanned || 0;
    let subSetCount = item.subSetScanned || 0;
    let setCount = item.setScanned || 0;
    if (mode === 'set') setCount += 1;
    else if (mode === 'subset') subSetCount += 1;
    else individual += 1;
    return { ...item, scannedCount: newScannedCount, individualScanned: individual, subSetScanned: subSetCount, setScanned: setCount, status: newStatus };
  });

  setItems(updatedItems);
}, [items, lastScanned, toggleModes, scanMode]);

  // ── Manual increment / quantity ────────────

  const onManualIncrement = (rowKey, delta) => {
    const updated = items.map(item => {
      if (item.rowKey !== rowKey) return item;

      const mode = toggleModes?.[rowKey] || 'single';
      const counts = computeScanCounts(item, mode, delta);

      if (counts.total > item.quantity) {
        showError('Too Many Items', `That would exceed the quantity for "${item.barcode}".`);
        return item;
      }

      return buildUpdatedItem(item, counts);
    });

    setItems(updated);
  };

  const onManualQuantityInput = (rowKey, qty) => {
    const updated = items.map(item => {
      if (item.rowKey !== rowKey) return item;

      const mode = toggleModes?.[rowKey] || 'single';
      const setQuantity = item.setQuantity || 1;

      let individual = item.individualScanned || 0;
      let setCount = item.setScanned || 0;
      let total = item.scannedCount || 0;

      if (mode === 'set') {
        setCount += qty;
        total += qty * setQuantity;
      } else if (mode === 'subset') {
        individual += qty * setQuantity;
        total += qty * setQuantity;
      } else {
        individual += qty;
        total += qty;
      }

      if (total > item.quantity) {
        showError('Too Many Items', `That would exceed the quantity for "${item.barcode}".`);
        return item;
      }

      return buildUpdatedItem(item, { individual, subSetCount: item.subSetScanned || 0, setCount, total });
    });

    setItems(updated);
  };

  const handleManualVerify = (rowKey) => {
    setItems(items.map(item =>
      item.rowKey !== rowKey ? item : {
        ...item,
        scannedCount: item.quantity,
        individualScanned: item.quantity,
        setScanned: 0,
        status: 'scanned',
      }
    ));
  };

  const handleToggleModeChange = (rowKey, mode) => {
    setToggleModes(prev => ({ ...prev, [rowKey]: mode }));
  };

  const handlePhotoChange = (rowKey, updatedPhotos) => {
    setItemPhotos(prev => ({ ...prev, [rowKey]: updatedPhotos }));
  };

  // ── Carton/set size override (same prompt style as PO Receiving) ──
  const handleEditCartonSize = (rowKey, barcode, currentValue) => {
    setCartonPrompt({ rowKey, barcode, currentValue });
  };

  const handleCartonSubmit = async (newValue) => {
    if (!newValue || newValue < 1 || !cartonPrompt) return;
    const { rowKey, barcode } = cartonPrompt;
    setCartonPrompt(null);

    // Reflect immediately in this session's items.
    setItems(prev => prev.map(item =>
      item.rowKey === rowKey ? { ...item, setQuantity: newValue } : item
    ));

    // Persist to Striven so future orders pick up the corrected size too —
    // same endpoint PO Receiving already uses for this exact custom field.
    try {
      const headers = await getAuthHeaders();
      await api.post('/api/items/update-carton-size', { barcodeValue: barcode, cartonSize: newValue }, { headers });
    } catch (err) {
      console.warn('Could not save carton size to Striven:', err);
      showError("Couldn't save to Striven", `The size is changed on this screen, but Striven did not save it, so the item will still have the old size next time. ${errorReason(err)}`);
    }
  };

  // ── API actions ────────────────────────────

  // Applies a saved_orders row from the backend onto the live scanning state —
  // shared by the auto-resume path in handleSubmit below.
  const applyLoadedDraft = (savedOrder) => {
    // Fallback assigns rowKey by index for drafts saved before rowKey existed —
    // item.rowKey (if already present) wins over the fallback via the spread order below.
    const loadedItems = JSON.parse(savedOrder.itemsJson || '[]').map((item, idx) => ({ rowKey: idx, ...item }));
    setItems(loadedItems);
    setToggleModes(JSON.parse(savedOrder.toggleModesJson || '{}'));
    setItemPhotos(JSON.parse(savedOrder.itemPhotosJson || '{}'));
    setOrderPhotos(JSON.parse(savedOrder.orderPhotosJson || '[]'));
    setResumedDraft({ savedAt: savedOrder.savedAt || null, expiresAt: savedOrder.expiresAt || null });
    setSubmitted(true);
  };

  // "expires in 6 days" / "expires today" / "expires tomorrow" — used by the
  // resumed-draft banner. Drafts are on a rolling 10-day clock from the last
  // Save or Load, enforced server-side; this just renders that countdown.
  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const msLeft = new Date(expiresAt).getTime() - Date.now();
    const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) return 'expires today';
    if (daysLeft === 1) return 'expires tomorrow';
    return `expires in ${daysLeft} days`;
  };

  // The original "Scan new order" flow — fetches fresh from Striven. Used both
  // when no draft exists yet, and from the "Start fresh instead" escape hatch.
  const fetchFreshFromStriven = async (headers) => {
    setLoadingMessage('Fetching order info...');

    const statusRes = await api.post('/api/get-order-status', { id: orderID }, { headers });
    const status = statusRes.data;
    setOrderStatus(status);

    if (status.level1Verified && !(user.role === 'Admin' || user.role === 'Verifier' || user.role === 'Supervisor')) {
      alert('This order has already been checked by the warehouse.');
      return;
    }

    const verifyCheck = await api.post('/api/is-verified', { id: orderID }, { headers });

    if (verifyCheck.data.verified) {
      if (user.role !== 'User') {
        const confirmUnverify = window.confirm('This order is fully verified. Unverify it?');
        if (confirmUnverify) {
          await api.post('/api/unverify-order', { id: orderID, name: user.name, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }, { headers });
          alert('Order successfully unverified.');
        }
      } else {
        alert('This order is fully verified and cannot be modified.');
      }
      return;
    }

    const res = await api.post('/api/get-dictionary', { id: orderID }, { headers });
    // rowKey (index-based) uniquely identifies each line item even when two rows
    // share the same barcode (e.g. the same item split across two order groups) —
    // everything below keys off rowKey instead of barcode for exactly that reason.
    const formattedData = res.data.map((raw, idx) => ({ ...formatItemFromApi(raw), rowKey: idx }));

    // Admin/Verifier at level-1 → skip to level-2 admin view
    if ((user.role === 'Admin' || user.role === 'Verifier' || user.role === 'Supervisor') && status.level1Verified && !status.level2Verified) {
      const level2Items = formattedData.map(item => ({
        ...item,
        scannedCount: item.quantity,
        status: 'scanned',
      }));

      setShowAdminLevel2(true);
      setSubmitted(true);
      setItems(level2Items);
      return;
    }

    setItems(formattedData);
    setStartedAt(new Date().toISOString());
    setResumedDraft(null);
    setSubmitted(true);
  };

  // Single entry point for the search screen. Auto-detects a saved draft for
  // this order number first and resumes it silently if one exists; only falls
  // through to a fresh Striven fetch when there's nothing to resume. Replaces
  // the old "Scan new order" / "Load saved order" pair so no one can hit the
  // wrong one and silently overwrite someone else's in-progress scan.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderID) { alert('Enter an order number.'); return; }
    setIsLoading(true);
    setLoadingMessage('Checking for a saved draft...');

    try {
      const headers = user.isMock
        ? { Authorization: 'Bearer MOCK_ACCESS_TOKEN' }
        : await getAuthHeaders();

      try {
        const draftRes = await api.get(`/api/load-order/${orderID}`, { headers });
        applyLoadedDraft(draftRes.data);
        return;
      } catch (draftErr) {
        if (draftErr.response?.status !== 404) {
          console.error('Draft lookup failed, falling back to a fresh fetch:', draftErr);
        }
        // 404 (no draft yet) or a lookup hiccup → fall through to the normal fetch below
      }

      await fetchFreshFromStriven(headers);
    } catch (error) {
      console.error('API error:', error.response?.data || error.message);
      if (error.response?.status === 404 || error.response?.status === 400 || error.response?.status === 500) {
        showError('Order Not Found', `No order was found for "${orderID}". Please check the order number and try again.`);
      } else {
        showError('Something went wrong', error.response?.data || error.message);
      }
      setItems([]);
    } finally {
      setLoadingMessage('Loading...');
      setIsLoading(false);
    }
  };

  // Escape hatch shown only once a draft has actually been auto-resumed —
  // discards it in favor of a clean fetch from Striven.
  const handleStartFresh = async () => {
    if (!window.confirm("Discard this saved draft and start the order over from Striven? This can't be undone.")) return;
    setIsLoading(true);
    try {
      const headers = user.isMock
        ? { Authorization: 'Bearer MOCK_ACCESS_TOKEN' }
        : await getAuthHeaders();
      await fetchFreshFromStriven(headers);
    } catch (error) {
      console.error('API error:', error.response?.data || error.message);
      showError('Something went wrong', error.response?.data || error.message);
    } finally {
      setLoadingMessage('Loading...');
      setIsLoading(false);
    }
  };

  // Lets Stats' "In-Progress Orders" list jump straight into a draft — App.js
  // passes the clicked order's ID down here once, and this resumes it exactly
  // like typing the number in and pressing Fetch would.
  const [autoLoadPending, setAutoLoadPending] = useState(false);

  useEffect(() => {
    if (initialOrderID) {
      setOrderID(initialOrderID);
      setAutoLoadPending(true);
    }
  }, [initialOrderID]);

  useEffect(() => {
    if (autoLoadPending && orderID === initialOrderID) {
      setAutoLoadPending(false);
      onInitialOrderIDHandled?.();
      handleSubmit({ preventDefault: () => {} });
    }
  }, [autoLoadPending, orderID]);

  const handleSaveOrder = async () => {
    if (!orderID) { alert('No order ID entered.'); return; }

    let sizeMB = null;
    try {
      setIsLoading(true);
      setLoadingMessage('Saving order...');

      // Photos taken before compression existed (or loaded from an old draft)
      // can be full-size camera images — shrink them first so the draft fits.
      const [smallItemPhotos, smallOrderPhotos] = await Promise.all([
        shrinkItemPhotos(itemPhotos),
        shrinkPhotoList(orderPhotos),
      ]);
      setItemPhotos(smallItemPhotos);
      setOrderPhotos(smallOrderPhotos);

      const body = { orderID, ...getOrderJson(smallItemPhotos, smallOrderPhotos) };
      sizeMB = JSON.stringify(body).length / 1_000_000;
      if (sizeMB > MAX_SAVE_MB) {
        alert(
          `This order is too big to save (${sizeMB.toFixed(1)} MB, limit ${MAX_SAVE_MB} MB) because of how many photos it has.\n\n` +
          'Remove a few photos and try Save again. Verifying the order still works as normal.'
        );
        return;
      }

      const headers = await getAuthHeaders();
      await api.post('/api/save-order', body, { headers });
      alert('Order saved successfully!');
    } catch (err) {
      console.error('Failed to save order:', err);
      const detail = err.response?.status
        ? `Error saving order (${err.response.status}): ${JSON.stringify(err.response.data)}`
        : `Couldn't reach the server to save this order. Check the wifi and try again.\n\n` +
          `Details: ${err.message} (code: ${err.code || 'unknown'}, browser online: ${navigator.onLine}` +
          `${sizeMB != null ? `, size: ${sizeMB.toFixed(1)} MB` : ''})`;
      alert(detail);

      // A true network error (no response at all) never reaches our own logs,
      // so it's otherwise invisible after the fact. Best-effort beacon — a
      // real outage will usually still let this small request through even
      // when the original save didn't, since it doesn't carry the full order payload.
      if (!err.response) {
        try {
          const beaconHeaders = await getAuthHeaders();
          await api.post('/api/client-error-log', {
            orderID,
            message: `${err.message}${sizeMB != null ? ` size=${sizeMB.toFixed(1)}MB` : ''}`,
            code: err.code,
            online: navigator.onLine,
            userAgent: navigator.userAgent,
          }, { headers: beaconHeaders });
        } catch { /* nothing more we can do if even this fails */ }
      }
    } finally {
      setIsLoading(false);
      setLoadingMessage('Loading...');
    }
  };


  const handleFullVerify = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage('Fetching order items...');
      await new Promise(resolve => setTimeout(resolve, 50));

      const headers = await getAuthHeaders();

      setLoadingMessage('Confirming order items...');
      await new Promise(resolve => setTimeout(resolve, 50));

      const res = await api.post('/api/get-dictionary', { id: orderID }, { headers });
      if (!res.data || res.data.length === 0) { alert('No items found for this order!'); return; }

      setCompletedItems(res.data.map(({ barcode, quantity }) => ({
        barcode, quantity, scannedCount: quantity, status: 'verified',
      })));

      setLoadingMessage('Submitting full verification...');
      await new Promise(resolve => setTimeout(resolve, 50));

      const fullVerifyRes = await api.post('/api/submit-verification', {
  id: orderID, fullVerify: true, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  itemCount: items.length,
  startedAt: startedAtRef.current,
}, { headers });
      setVerificationComplete(true);
      setShowAdminLevel2(true);
      alert('Order has been fully verified!' +
        (fullVerifyRes?.data?.warning ? `\n\n${fullVerifyRes.data.warning}` : ''));
      resetApp();
    } catch (err) {
      console.error('Full verify error:', err.response?.data || err.message);
      alert('Failed to fully verify order.');
    } finally {
      setIsLoading(false);
      setLoadingMessage('Loading...');
    }
  };

  const handleUnverify = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage('Processing unverify...');
      await new Promise(resolve => setTimeout(resolve, 50));

      const headers = await getAuthHeaders();
      const res = await api.post('/api/unverify-order', { id: orderID, name: user.name, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }, { headers });

      if (res.status !== 200) { alert('Failed to unverify order.'); return; }

      alert('Order has been unverified.');
      resetApp();
    } catch (err) {
      console.error(err);
      alert('Failed to unverify order.');
    } finally {
      setIsLoading(false);
      setLoadingMessage('Loading...');
    }
  };
const handleGeneratePdf = async () => {
  // Capture synchronously before any async calls
  const capturedStartedAt = startedAt || new Date().toISOString();
  const capturedItemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const capturedUniqueItemCount = items.length;

  setIsLoading(true);
  try {
    setLoadingMessage('Fetching order info...');

    const headers = await getAuthHeaders();

    const credsRes = await api.get('/api/config/credentials', { headers });
    const freshPdfEmail = credsRes.data.pdfEmail || '';

if (pdfDestinations.includes('email') && !freshPdfEmail) {
  alert('No PDF delivery email configured. Please contact your admin.');
  return;
}

    const response = await api.post('/api/get-order-info', { id: orderID }, { headers });
    const orderInfo = response.data;

    setLoadingMessage('Generating PDF document...');
    const now = new Date();
    const formattedDate = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${now.getFullYear()}`;
    const pdfBlob = await PdfGen(orderID, items, orderInfo, itemPhotos, orderPhotos, user.name, pdfResolution, setLabel);
    const fileName = `${formattedDate}_2Check_SO_${orderID}_${user.name}.pdf`;

// ── Local download (gated) ──────────────────────────────
if (pdfDestinations.includes('local')) {
  const blobUrl = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

// ── Base64 for verification endpoint ───────────────────
const pdfBase64 = await new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(pdfBlob);
});

setLoadingMessage('Submitting...');

const provider = user.authProvider;

// ── SharePoint / Drive upload (gated) ──────────────────
let pdfUrl = '';
if (pdfDestinations.includes('sharepoint')) {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const uploadRes = await (provider === 'google'
      ? api.post('/api/upload-google-drive', formData)
      : api.post('/api/upload-pdf', formData));
    pdfUrl = uploadRes.data?.webUrl
          || uploadRes.data?.url
          || uploadRes.data?.webViewLink
          || '';
  } catch (uploadErr) {
    if (uploadErr.response?.status === 401) {
      const service = provider === 'google' ? 'Google Drive' : 'SharePoint';
      const fix = provider === 'google'
        ? 'Your Google connection has expired. An admin can reconnect it under Admin Panel → Credentials → Google account connection.'
        : 'Your SharePoint connection has expired. An admin may need to update the Azure credentials.';
      throw new Error(`${service} upload failed: ${fix}`);
    }
    throw uploadErr;
  }
}
const emailRequested = pdfDestinations.includes('email') && !!freshPdfEmail;
const capturedIndividualCount = items.reduce((sum, i) => sum + (i.individualScanned || 0), 0);
const capturedSubSetCount     = items.reduce((sum, i) => sum + (i.subSetScanned || 0), 0);
const capturedSetCount        = items.reduce((sum, i) => sum + (i.setScanned || 0), 0);
// ── Striven attachment (gated) ─────────────────────────
const verifyPayload = {
  id: String(orderID),
  name: String(user.name),
  FullVerify: user.role === 'Admin' || user.role === 'Verifier' || user.role === 'Supervisor',
  Unverify: false,
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  itemCount: capturedItemCount,
  uniqueItemCount: capturedUniqueItemCount,
  startedAt: capturedStartedAt,
  orderName: orderInfo?.orderName ?? '',
  pdfUrl,
  pdfFileName: fileName, // used as the link text in Striven's internal note, regardless of destination
  ...(pdfDestinations.includes('striven') ? { pdfBase64 } : {}),
  individualCount: capturedIndividualCount,
subSetCount: capturedSubSetCount,
setCount: capturedSetCount,
  // Extra detail for the Logs page only — the backend stores it as-is and
  // adds what only it knows (whether the Striven attachment went through).
  detailsJson: JSON.stringify({
    photoCount: Object.values(itemPhotos).reduce((n, p) => n + (p?.length || 0), 0) + orderPhotos.length,
    items: items.map(i => ({
      name: i.barcode,
      expected: i.quantity,
      scanned: i.scannedCount || 0,
      photos: (itemPhotos[i.rowKey] || []).length,
    })),
    delivery: {
      download: pdfDestinations.includes('local'),
      storage: pdfDestinations.includes('sharepoint') ? (provider === 'google' ? 'Google Drive' : 'SharePoint') : null,
      email: emailRequested ? 'pending' : null,
      emailTo: emailRequested ? freshPdfEmail : null,
    },
  }),
};

// ── Email (gated) ──────────────────────────────────────
const deliveryPromises = [api.post('/api/submit-verification', verifyPayload, { headers })];
let emailRequest = null;

if (pdfDestinations.includes('email')) {
  if (!freshPdfEmail) {
    alert('No PDF delivery email configured. Please contact your admin.');
    // don't hard-return — still submit verification
  } else {
    const emailForm = new FormData();
    emailForm.append('email', freshPdfEmail);
    emailForm.append('orderID', orderID);
    emailForm.append('pdf', file);
    emailRequest = api.post('/api/send-pdf-email', emailForm, { headers });
    deliveryPromises.push(emailRequest);
  }
}

let verifyRes;
try {
  [verifyRes] = await Promise.all(deliveryPromises);
} finally {
  // The email goes out alongside the Striven submit, so the verification row
  // is written before its outcome is known — report it once both have settled
  // (a failed email rejects Promise.all while the submit may still be running).
  // Best-effort and not awaited: it must never hold up or fail a verification.
  if (emailRequest) {
    Promise.allSettled(deliveryPromises)
      .then(([, emailResult]) => api.post('/api/verification-delivery',
        { orderID: String(orderID), emailSent: emailResult.status === 'fulfilled' }, { headers }))
      .catch(() => {});
  }
}
const strivenSynced = verifyRes?.data?.strivenSynced !== false;
// Striven refused the order, so the backend finished the rest and its warning
// says what to do; "sync is off" would be the wrong explanation.
const needsManualMark = verifyRes?.data?.needsManualMark === true;

// ── Success message ────────────────────────────────────
const sent = [];
if (pdfDestinations.includes('local'))      sent.push('downloaded');
if (pdfDestinations.includes('sharepoint')) sent.push(`uploaded to ${provider === 'google' ? 'Google Drive' : 'SharePoint'}`);
if (pdfDestinations.includes('striven') && strivenSynced) sent.push('attached to Striven order');
if (pdfDestinations.includes('email'))      sent.push('emailed');

alert(`PDF ${sent.join(', ')} successfully!` +
  (strivenSynced || needsManualMark ? '' : '\n\nStriven sync is off, so this order was NOT updated in Striven. Mark it there by hand.') +
  (verifyRes?.data?.warning ? `\n\n${verifyRes.data.warning}` : ''));

    setLoadingMessage('Finishing up...');
    await new Promise(resolve => setTimeout(resolve, 500));
    setVerificationComplete(true);

  } catch (error) {
    console.error('Error in PDF generation/verification:', error.response?.data || error.message);
    alert(error.response?.data || 'Something went wrong.');
  } finally {
    setIsLoading(false);
    if (user.role === 'Admin' || user.role === 'Verifier' || user.role === 'Supervisor') resetApp();
    setAwaitingFinalConfirmation(true);
  }
};

  const handleLogout = async () => {
    try {
      const authProvider = localStorage.getItem('authProvider');

      if (authProvider === 'microsoft') {
        const activeAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
        if (activeAccount) instance.setActiveAccount(null);
      }

      if (authProvider === 'google') {
        const token = localStorage.getItem('authToken');
        if (token) {
          await fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, { method: 'POST' });
        }
      }

      localStorage.removeItem('authProvider');
      localStorage.removeItem('authToken');
      onLogout();
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      localStorage.clear();
      onLogout();
      window.location.href = '/';
    }
  };

  // ── Render guards ──────────────────────────

  // Post-completion screen (all roles)
  if (awaitingFinalConfirmation || verificationComplete) {
    const headingText = (user?.role === 'Verifier' || user?.role === 'Admin')
      ? 'Order Now Ready for Packaging'
      : 'Order Ready for Full Verification';

    return (
      <div className="admin-level2-container" style={{ position: 'relative' }}>
        <LoadingOverlay isLoading={isLoading} message={loadingMessage} />
        <h2 style={{ color: 'black' }}>{headingText}</h2>
        {items.length > 0 ? <CompletedOrderTable data={items} /> : <div />}
        <button
          className="done-order-button"
          onClick={() => { resetApp(); setAwaitingFinalConfirmation(false); }}
        >
          Return to Order Search page
        </button>
      </div>
    );
  }

  // Admin / Verifier level-2 view
  if (showAdminLevel2 && (user.role === 'Admin' || user.role === 'Verifier' || user.role === 'Supervisor')) {
    return (
      <div className="admin-level2-container" style={{ position: 'relative' }}>
        <LoadingOverlay isLoading={isLoading} message={loadingMessage} />
        <h2 style={{ color: 'black' }}>Order #{orderID}</h2>
        <p style={{ color: 'black' }}>Checked: {orderStatus?.verifiedBy}</p>
        <p style={{ color: 'black' }}>On: {new Date(orderStatus?.verifiedDate).toLocaleString()}</p>
        {items.length > 0 ? <CompletedOrderTable data={items} /> : <div />}
        <div className="verification-buttons">
          <button className="full-verify-button" onClick={handleFullVerify}>Fully Verify Order</button>
          <button className="unverify-button" onClick={handleUnverify}>Unverify Order</button>
        </div>
      </div>
    );
  }

  // ── Main render ────────────────────────────
  return (
    <>
      <LoadingOverlay isLoading={isLoading} message={loadingMessage} />
      {/* Settings modal */}
      <Modal
        isOpen={showSettingsModal}
        onRequestClose={() => setShowSettingsModal(false)}
        contentLabel="User Settings"
        className="settings-modal"
        overlayClassName="modal-overlay"
      >
        <button className="modal-close-btn" onClick={() => setShowSettingsModal(false)}>×</button>
        <h2 className="modal-title">User settings</h2>
        <div className="modal-field"><span className="modal-label">Name</span>{user.name}</div>
        <div className="modal-field"><span className="modal-label">Role</span>{user.role}</div>

{user.role === 'Admin' && (
  <AdminPanel
    currentUserEmail={user.email}
    onClose={() => setShowSettingsModal(false)}
    onPdfEmailChange={setPdfEmail}
    onLogout={handleLogout}
  />
)}

{user.role !== 'Admin' && (
  <button className="modal-logout-btn" onClick={handleLogout}>Log out</button>
)}
      </Modal>

      {/* Page body */}
      <div className="form-page">
        {verificationComplete ? (
          <div className="verify-success-card">
            <div className="verify-success-icon">✅</div>
            <h1 className="verify-success-title">Verification successful</h1>
            <p className="verify-success-sub">The order has been verified and updated successfully.</p>
            <button className="topbar-action-btn" onClick={resetApp}>Return to order search</button>
          </div>
        ) : (
          <>
            {/* Order search */}
{!submitted && (
  <div className="landing-search-wrap">
    <div className="search-intro">
      <h1 className="search-title">Find an order</h1>
      <p className="search-subtitle">
        Enter an order number — if it has a saved draft, you'll pick up right where it was left off.
      </p>
    </div>

    <div className="search-card">
      {/* ← no className="form-app" here */}
      <form onSubmit={handleSubmit}>
        <div className="search-input-wrap">
          <span className="search-input-icon">⌗</span>
          <input
            type="text"
            value={orderID}
            onChange={e => setOrderID(e.target.value)}
            placeholder="Order number"
          />
        </div>

        <div className="search-btn-row">
          <input type="submit" value="Open order" />
        </div>
      </form>
    </div>
  </div>
)}

            {/* Order dashboard */}
{submitted && items.length > 0 && (
  <div className="order-dashboard">

{/* Change the outer div to: */}
<div style={{
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '1rem',
  gap: 12,
  flexWrap: 'nowrap',   // keep on one line — it has room
}}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button className="topbar-back-btn" onClick={resetApp}>
          ← Back to search
        </button>
        <h2 className="order-heading" style={{ margin: 0 }}>Order #{orderID}</h2>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="topbar-action-btn secondary"
          onClick={async () => {
            if (useCamera && cameraRef.current) await cameraRef.current.stopScanner();
            setUseCamera(!useCamera);
          }}
        >
          {useCamera ? '📷 Manual mode' : '📷 Camera mode'}
        </button>
        <button className="topbar-action-btn" onClick={handleSaveOrder}>
          Save
        </button>
      </div>
    </div>
{resumedDraft && (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    background: '#fff7ed',
    border: '1px solid #f3caa1',
    borderRadius: 8,
    padding: '8px 14px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    color: '#c2410c',
  }}>
    <span>
      ⚠ Resumed a saved draft{resumedDraft.savedAt ? ` from ${new Date(resumedDraft.savedAt).toLocaleString()}` : ''}.
      {formatExpiry(resumedDraft.expiresAt) ? ` Draft ${formatExpiry(resumedDraft.expiresAt)} if not saved again.` : ''}
    </span>
    <button
      type="button"
      onClick={handleStartFresh}
      style={{
        background: 'none', border: 'none', padding: 0, margin: 0,
        color: '#c2410c', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit',
      }}
    >
      Start fresh instead
    </button>
  </div>
)}
{useCamera && (
  <div style={{ display: 'flex', justifyContent: 'center' }}>
<CameraScan ref={cameraRef} onDetected={handleCamScan} />
  </div>
)}

<TableFormat
  data={items}
  onManualVerify={handleManualVerify}
  onManualIncrement={onManualIncrement}
  onManualQuantityInput={onManualQuantityInput}
  onPhotoChange={handlePhotoChange}
  itemPhotos={itemPhotos}
  toggleModes={toggleModes}
  onToggleModeChange={handleToggleModeChange}
  onEditCartonSize={handleEditCartonSize}
  setLabel={setLabel}  // 👈 add this
  maxPhotosPerItem={maxPhotosPerItem}
  showWarehouseLocation={showWarehouseLocation}
/>

                {/* Submit section — shown once all items scanned */}
                {items.every(i => i.status === 'scanned') && (
                  <div className="pdf-submit-wrapper">
                    {!confirmedCompletion ? (
                      <button className="topbar-action-btn" onClick={() => setConfirmedCompletion(true)}>
                        ✅ I'm done scanning!
                      </button>
                    ) : (
                      <>
                        <button className="link-btn" onClick={() => setConfirmedCompletion(false)}>
                          ← Go back to scanning
                        </button>
<PhotoUpload
  itemKey="order"
  photos={orderPhotos}
  onPhotoChange={(_, updated) => setOrderPhotos(updated)}
  maxPhotos={maxPhotosPerItem}
  inline
  onBusyChange={setOrderPhotoBusy}
/>

                        <button
                          className="topbar-action-btn"
                          style={{ marginTop: '1.25rem' }}
                          disabled={!canSubmitOrder() || orderPhotoBusy}
                          onClick={() => {
                            if (!canSubmitOrder()) { alert(getPhotoValidationErrors().join('\n')); return; }
                            handleGeneratePdf();
                          }}
                        >
                          Generate PDF and submit
                        </button>

                        {pdfEmail && (
                          <p className="pdf-email-notice">
                            <strong>PDF will be sent to:</strong> {pdfEmail}
                          </p>
                        )}

                        {!canSubmitOrder() && (
                          <div className="validation-errors">
                            {getPhotoValidationErrors().map((err, i) =>
                              err.type === 'items' ? (
                                <div key={i}>
                                  ❗ Missing photo for:
                                  <ul>{err.items.map((barcode, j) => <li key={j}>{barcode}</li>)}</ul>
                                </div>
                              ) : (
                                <div key={i}>❗ {err.message}</div>
                              )
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

{alertModal && (
  <div
    style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 3000,
    }}
    onClick={handleCloseModal}
  >
    <div
      style={{
        background: '#fff', borderRadius: 12, padding: '28px 32px',
        width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        textAlign: 'center',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ fontSize: 36, marginBottom: 12 }}>❌</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 8 }}>
        {alertModal.title}
      </div>
      <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 20 }}>
        {alertModal.message}
      </div>
      <button
        autoFocus
        onClick={handleCloseModal}
        style={{
          padding: '9px 28px',
          background: '#dc2626', color: '#fff',
          border: 'none', borderRadius: 6,
          fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}
      >
        Got it
      </button>
    </div>
  </div>
)}

{cartonPrompt && (
  <div
    style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 3000,
    }}
    onClick={() => setCartonPrompt(null)}
  >
    <div
      style={{
        background: '#fff', borderRadius: 12, padding: '28px 32px',
        width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        textAlign: 'center',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 8 }}>
        Update {setLabel.toLowerCase()} size
      </div>
      <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 16 }}>
        How many units are in a {setLabel.toLowerCase()} of <strong>{cartonPrompt.barcode}</strong>?
      </div>
      <input
        ref={cartonInputRef}
        type="number"
        min="1"
        autoFocus
        defaultValue={cartonPrompt.currentValue || ''}
        style={{
          width: '100%', padding: '8px 10px', fontSize: 15,
          border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 16,
          textAlign: 'center', boxSizing: 'border-box',
        }}
        onKeyDown={e => {
          if (e.key !== 'Enter') return;
          const val = parseInt(cartonInputRef.current?.value, 10);
          if (!val || val < 1) return;
          handleCartonSubmit(val);
        }}
      />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button
          onClick={() => setCartonPrompt(null)}
          style={{
            padding: '9px 20px', background: '#f3f4f6', color: '#374151',
            border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            const val = parseInt(cartonInputRef.current?.value, 10);
            if (!val || val < 1) return;
            handleCartonSubmit(val);
          }}
          style={{
            padding: '9px 20px', background: '#218a3d', color: '#fff',
            border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Save
        </button>
      </div>
    </div>
  </div>
)}
    </>
  );
}

export default FormFunction;